/* eslint-disable no-unused-vars */
// Blog Search Header functionality - handles Swiper and Search Dropdown

class BlogSearchDropdown {
  constructor() {
    this.dropdown = document.getElementById('blog-search-dropdown');
    this.searchInput = document.querySelector(
      '.blog-search-form input[name="q"]'
    );
    this.loading = document.getElementById('blog-search-loading');
    this.loadingInput = document.getElementById('blog-search-loading-input');
    this.submitBtn = document.getElementById('blog-search-submit-btn');
    this.articlesList = document.getElementById('blog-articles-list');
    this.categoryLinks = document.getElementById(
      'blog-search-category-links'
    );
    this.noResults = document.getElementById('blog-search-no-results');

    // Kategorie matching: Shopify's predictive search only indexes
    // title/body/tags, not the rezept_kategorie metaobjects. Queries that
    // match a category name are additionally resolved via the
    // article-filter proxy (same source as the blog filters).
    const dropdownData = this.dropdown?.dataset || {};
    this.blogUrl = dropdownData.blogUrl || '';
    this.locale = dropdownData.locale || '';
    this.categoryLinkTemplate =
      dropdownData.categoryLinkTemplate || '__CATEGORY__';
    // Everything before "/blogs/" is the Liquid-rendered locale prefix.
    this.localePrefix = this.blogUrl.includes('/blogs/')
      ? this.blogUrl.split('/blogs/')[0]
      : '';
    this.proxyUrl = '/apps/article-filter/articles';
    this.kategorienUrl = '/apps/article-filter/kategorien';
    // Liquid-rendered map (capped at 50 entries per metaobject list) as the
    // fallback; the full localized map is loaded from the proxy on first use.
    this.kategorien = BlogSearchDropdown.parseKategorien();
    this.kategorienLoaded = null;

    this.searchTimeout = null;
    this.currentQuery = '';
    this.abortController = null;
    // Use Shopify's global routes or fall back to data attribute
    let routesRoot =
      window.Shopify?.routes?.root || this.dropdown?.dataset?.routesRoot || '/';
    routesRoot = routesRoot.trim();
    if (!routesRoot.endsWith('/')) {
      routesRoot += '/';
    }
    this.routesRoot = routesRoot;

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupKeyboardNavigation();
  }

  bindEvents() {
    if (!this.searchInput) return;

    // Input events
    this.searchInput.addEventListener('input', e => {
      const value = e.target.value.trim();
      this.handleInput(value);
    });

    // Click outside to close
    document.addEventListener('click', e => {
      if (
        this.isOpen &&
        !this.dropdown.contains(e.target) &&
        !this.searchInput.contains(e.target)
      ) {
        this.close();
      }
    });

    // Escape key to close
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setupKeyboardNavigation() {
    this.dropdown.addEventListener('keydown', e => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;

      const focusableElements = this.dropdown.querySelectorAll(
        'a[href], button:not([disabled])'
      );

      if (focusableElements.length === 0) return;

      const currentIndex = Array.from(focusableElements).indexOf(
        document.activeElement
      );

      e.preventDefault();
      if (e.key === 'ArrowDown') {
        const nextIndex =
          currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        focusableElements[nextIndex]?.focus();
      } else {
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        focusableElements[prevIndex]?.focus();
      }
    });
  }

  handleInput(value) {
    this.currentQuery = value;

    if (this.currentQuery.length < 2) {
      this.abortPendingRequest();
      this.close();
      return;
    }

    // Debounce search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 300);
  }

  triggerSearch(query) {
    // Override current query and perform search immediately
    // Clear any pending debounced search
    clearTimeout(this.searchTimeout);
    this.currentQuery = query;
    this.performSearch();
  }

  abortPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async performSearch() {
    if (!this.currentQuery || this.currentQuery.length < 2) {
      this.abortPendingRequest();
      this.close();
      return;
    }

    // Abort any pending request before starting a new one
    this.abortPendingRequest();

    // Create new AbortController for this request
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.showLoading();

    try {
      // Search only for articles (with locale-aware path)
      const searchUrl = `${this.routesRoot}search/suggest.json?q=${encodeURIComponent(
        this.currentQuery
      )}&resources[type]=article&resources[limit]=10`;
      await this.ensureKategorien();
      if (signal.aborted) return;
      const categoryMatches = this.matchCategories(this.currentQuery);

      const [response, categoryArticles] = await Promise.all([
        fetch(searchUrl, { signal }),
        this.fetchCategoryArticles(categoryMatches, signal),
      ]);

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();

      // Check if request was aborted before updating UI
      if (signal.aborted) {
        return;
      }

      this.displayResults(data, categoryArticles, categoryMatches);
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError') {
        return;
      }
      // Check if still current request (not aborted)
      if (!signal.aborted) {
        this.showError();
      }
    } finally {
      // Only hide loading if this is still the current request
      if (!signal.aborted) {
        this.hideLoading();
      }
    }
  }

  get isOpen() {
    return !this.dropdown.classList.contains('hidden');
  }

  displayResults(data, categoryArticles = [], categoryMatches = []) {
    const predictive = data.resources?.results?.articles || [];
    const articles = BlogSearchDropdown.mergeArticles(
      categoryArticles,
      predictive
    ).slice(0, 10);

    if (articles.length === 0) {
      this.showNoResults();
      return;
    }

    this.displayArticles(articles);
    this.displayCategoryLinks(categoryMatches);
    this.open();
  }

  /* ---------------------------------------------------------------- */
  /* Kategorie matching via article-filter proxy                        */
  /* ---------------------------------------------------------------- */

  static parseKategorien() {
    const script = document.querySelector('[data-blog-search-kategorien]');
    if (!script) return [];
    let map = {};
    try {
      map = JSON.parse(script.textContent) || {};
    } catch (error) {
      return [];
    }
    return Object.keys(map)
      .filter(handle => map[handle] && map[handle].name)
      .map(handle => ({
        handle,
        name: map[handle].name,
        param: map[handle].param || 'category',
        norm: BlogSearchDropdown.normalize(map[handle].name),
      }));
  }

  /**
   * Load the full localized category map from the article-filter proxy
   * once; entries replace/extend the Liquid-rendered fallback.
   */
  ensureKategorien() {
    if (!this.kategorienLoaded) {
      const query = new URLSearchParams();
      if (this.locale) query.set('locale', this.locale);
      this.kategorienLoaded = fetch(`${this.kategorienUrl}?${query.toString()}`)
        .then(response => (response.ok ? response.json() : null))
        .then(data => {
          const map = data && data.kategorien;
          if (!map || typeof map !== 'object') return;
          const paramFor = {
            mahlzeit: 'meal',
            lebensmittel: 'food',
          };
          const merged = new Map(
            this.kategorien.map(cat => [cat.handle, cat])
          );
          Object.keys(map).forEach(handle => {
            const entry = map[handle];
            if (!entry || !entry.name) return;
            merged.set(handle, {
              handle,
              name: entry.name,
              param: paramFor[entry.gruppe] || 'category',
              norm: BlogSearchDropdown.normalize(entry.name),
            });
          });
          this.kategorien = Array.from(merged.values());
        })
        .catch(() => {
          // Proxy unavailable: keep the Liquid fallback map.
        });
    }
    return this.kategorienLoaded;
  }

  static normalize(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /**
   * Categories whose localized name matches the query. Prefix matches win
   * over "query contains category name" matches; max. 3 categories.
   */
  matchCategories(query) {
    const q = BlogSearchDropdown.normalize(query);
    if (q.length < 2) return [];

    return this.kategorien
      .map(cat => {
        let score = 0;
        if (cat.norm === q) {
          score = 3;
        } else if (
          q.length >= 3 &&
          (cat.norm.startsWith(q) ||
            cat.norm.split(' ').some(word => word.startsWith(q)))
        ) {
          score = 2;
        } else if (cat.norm.length >= 4 && q.includes(cat.norm)) {
          score = 1;
        }
        return { cat, score };
      })
      .filter(entry => entry.score > 0)
      .sort(
        (a, b) => b.score - a.score || a.cat.norm.length - b.cat.norm.length
      )
      .slice(0, 3)
      .map(entry => entry.cat);
  }

  async fetchCategoryArticles(matches, signal) {
    if (!matches.length) return [];

    // One request per URL param group (category / meal / food); handles
    // within a group are OR-ed by the proxy.
    const groups = {};
    matches.forEach(cat => {
      groups[cat.param] = groups[cat.param] || [];
      groups[cat.param].push(cat.handle);
    });

    const requests = Object.keys(groups).map(async param => {
      const query = new URLSearchParams();
      query.set(param, groups[param].join(','));
      if (this.locale) query.set('locale', this.locale);
      query.set('page', '1');
      try {
        const response = await fetch(`${this.proxyUrl}?${query.toString()}`, {
          signal,
        });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data.articles) ? data.articles : [];
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return [];
      }
    });

    const results = await Promise.all(requests);
    const seen = new Set();
    const articles = [];
    results.flat().forEach(article => {
      if (!article || seen.has(article.handle)) return;
      seen.add(article.handle);
      const kategorien = Array.isArray(article.kategorien)
        ? article.kategorien
        : [];
      articles.push({
        handle: article.handle,
        // Proxy URLs are unprefixed; the Liquid-rendered blog.url carries
        // the active locale prefix.
        url: this.localePrefix + (article.url || ''),
        title: article.title || '',
        image: article.image || '',
        tags: kategorien
          .map(handle => {
            const cat = this.kategorien.find(k => k.handle === handle);
            return cat ? cat.name : '';
          })
          .filter(Boolean),
      });
    });
    return articles;
  }

  /** Category hits first, then predictive hits not already listed. */
  static mergeArticles(categoryArticles, predictive) {
    const seenHandles = new Set();
    const seenTitles = new Set();
    const merged = [];
    [...categoryArticles, ...predictive].forEach(article => {
      if (!article) return;
      const handle = article.handle || '';
      const title = BlogSearchDropdown.normalize(
        BlogSearchDropdown.decodeHtml(article.title)
      );
      if (
        (handle && seenHandles.has(handle)) ||
        (title && seenTitles.has(title))
      ) {
        return;
      }
      if (handle) seenHandles.add(handle);
      if (title) seenTitles.add(title);
      merged.push(article);
    });
    return merged;
  }

  displayCategoryLinks(matches) {
    if (!this.categoryLinks) return;
    this.categoryLinks.innerHTML = '';
    if (!matches.length || !this.blogUrl) {
      this.categoryLinks.classList.add('hidden');
      return;
    }
    matches.forEach(cat => {
      const link = document.createElement('a');
      link.href = `${this.blogUrl}?${encodeURIComponent(
        cat.param
      )}=${encodeURIComponent(cat.handle)}`;
      link.className =
        'inline-flex items-center border border-aubergine/30 px-3 py-1.5 p-small text-aubergine hover:bg-aubergine/5 transition-colors duration-200';
      link.textContent = this.categoryLinkTemplate.replace(
        '__CATEGORY__',
        BlogSearchDropdown.decodeHtml(cat.name)
      );
      this.categoryLinks.appendChild(link);
    });
    this.categoryLinks.classList.remove('hidden');
  }

  displayArticles(articles) {
    this.articlesList.innerHTML = '';

    articles.forEach(article => {
      const articleElement = document.createElement('a');
      articleElement.href = article.url;
      articleElement.className =
        'blog-search-article-card flex items-start p-3 hover:bg-light-gray transition-colors duration-200';

      const imageUrl = article.image || '';
      const transformedImageUrl = imageUrl
        ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=128&height=128&crop=center&format=webp`
        : '';

      const decodedTitle = BlogSearchDropdown.decodeHtml(article.title);
      const escapedTitle = BlogSearchDropdown.escapeHtml(decodedTitle);

      articleElement.innerHTML = `
      <div class="w-16 h-16 mr-4 flex items-center justify-center overflow-hidden aspect-square">
          ${
            transformedImageUrl
              ? `<img src="${transformedImageUrl}" alt="${escapedTitle}" class="w-full h-full object-cover" width="64" height="64">`
              : `<svg class="w-8 h-8 text-aubergine/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>`
          }
        </div>
        <div class="flex flex-col w-fit">
          <div class="link-medium text-aubergine mb-1 w-fit link-hover-bold">${escapedTitle}</div>
          ${
            article.tags && article.tags.length > 0
              ? (() => {
                  const filteredTags = article.tags.filter(
                    tag => !tag.startsWith('Category_Rezept')
                  );
                  return filteredTags.length > 0
                    ? `<div class="flex flex-wrap gap-1 mt-1 w-fit">
                        ${filteredTags
                          .slice(0, 3)
                          .map(tag => {
                            const decodedTag =
                              BlogSearchDropdown.decodeHtml(tag);
                            const escapedTag = BlogSearchDropdown.escapeHtml(
                              decodedTag.length > 15
                                ? decodedTag.slice(0, 15)
                                : decodedTag
                            );
                            return `<span class="p-small text-aubergine/50">${escapedTag}</span>`;
                          })
                          .join('')}
                      </div>`
                    : '';
                })()
              : ''
          }
        </div>
      `;
      this.articlesList.appendChild(articleElement);
    });
  }

  showLoading() {
    this.hideAllSections();
    this.loading.classList.remove('hidden');
    if (this.loadingInput) {
      this.loadingInput.classList.remove('hidden');
    }
  }

  hideLoading() {
    this.loading.classList.add('hidden');
    if (this.loadingInput) {
      this.loadingInput.classList.add('hidden');
    }
  }

  showNoResults() {
    this.hideAllSections();
    this.noResults.classList.remove('hidden');
  }

  showError() {
    this.hideAllSections();
    this.noResults.innerHTML = `<p class="text-red-500 text-lg">${
      this.dropdown?.dataset?.errorMessage || 'Error'
    }</p>`;
    this.noResults.classList.remove('hidden');
  }

  hideAllSections() {
    this.noResults.classList.add('hidden');
    this.loading.classList.add('hidden');
    if (this.categoryLinks) {
      this.categoryLinks.classList.add('hidden');
    }
  }

  open() {
    this.dropdown.classList.remove('hidden');
  }

  close() {
    this.dropdown.classList.add('hidden');
  }

  static escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  static decodeHtml(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  static announceToScreenReader(message) {
    // Create announcement element for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    // Remove announcement after screen reader has time to read it
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Swiper for blog search header
  const blogSearchSwiper = document.querySelector('.blog-search-swiper');

  if (blogSearchSwiper) {
    // eslint-disable-next-line no-new, no-undef
    new Swiper('.blog-search-swiper', {
      loop: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      spaceBetween: 0,
      slidesPerView: 1,
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
        dynamicBullets: false,
      },
      grabCursor: true,
      keyboard: {
        enabled: true,
      },
      mousewheel: {
        invert: false,
        forceToAxis: true,
      },
      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      },
    });
  }

  // Initialize search dropdown
  const blogSearchForm = document.querySelector('.blog-search-form');
  if (blogSearchForm) {
    const blogSearchDropdown = new BlogSearchDropdown();
    // Store reference to prevent garbage collection
    window.blogSearchDropdown = blogSearchDropdown;

    // Handle search form submission
    blogSearchForm.addEventListener('submit', e => {
      e.preventDefault();

      const searchInput = blogSearchForm.querySelector('input[name="q"]');
      const query = searchInput.value.trim();

      if (query === '') {
        return;
      }

      // Trigger search dropdown instead of navigating to search page
      if (searchInput.value !== query) {
        searchInput.value = query;
      }
      // Trigger the search
      blogSearchDropdown.triggerSearch(query);
    });
  }
});
